const POST_AUTH_RETURN_TO_KEY = "lightly_post_login_redirect";
const ORG_CONTEXT_SESSION_KEY = "lightly_org_slug";
const RETURN_PATH_SESSION_KEY = "lightly_return_path";
const ORG_CONTEXT_LOCAL_KEY = "lightly_last_org_slug";
const RETURN_PATH_LOCAL_KEY = "lightly_last_return_path";
const PWA_STANDALONE_SESSION_KEY = "lightly_pwa_standalone";
const PWA_STANDALONE_LOCAL_KEY = "lightly_last_pwa_standalone";

const RESERVED_FIRST_SEGMENTS = new Set([
  "app",
  "me",
  "announcements",
  "admin",
  "api",
  "auth",
  "login",
  "logout",
  "platform",
  "register",
  "signup",
  "verify-phone",
  "connect-profile",
]);

function safeDecode(value) {
  if (!value) return "";
  try {
    return decodeURIComponent(value);
  } catch {
    return String(value || "");
  }
}

function normalizeLocationLike(input) {
  if (!input) return "";
  if (typeof input === "string") return input;

  if (input.pathname) {
    return `${input.pathname}${input.search || ""}${input.hash || ""}`;
  }

  if (input.from) return normalizeLocationLike(input.from);
  return "";
}

function getStorageSafe(storage, key) {
  try {
    return storage?.getItem?.(key) || "";
  } catch {
    return "";
  }
}

function setStorageSafe(storage, key, value) {
  try {
    if (value) storage?.setItem?.(key, value);
  } catch {
    /* ignore storage failures */
  }
}

function pathSegments(path = "") {
  return String(path || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean);
}



function isStandaloneContext() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
}

export function inferOrgSlugFromPath(path = "") {
  const segments = pathSegments(path);
  const first = segments[0] || "";
  const second = segments[1] || "";
  const third = segments[2] || "";

  if (first.toLowerCase() === "platform" && second.toLowerCase() === "o" && third) {
    return third;
  }
  if (first.toLowerCase() === "admin" && second) {
    return second;
  }
  if (first.toLowerCase() === "o" && second) {
    return second;
  }
  if (first.toLowerCase() === "dashboard" && second) {
    return second;
  }
  if (!first || RESERVED_FIRST_SEGMENTS.has(first.toLowerCase())) return "";
  return first;
}

function isAuthRoute(pathname) {
  const clean = String(pathname || "/")
    .replace(/\/+$/g, "")
    .toLowerCase();

  return [
    "/login",
    "/register",
    "/signup",
    "/platform/login",
    "/platform/signup",
  ].includes(clean || "/");
}

export function getCurrentPath(location) {
  return normalizeLocationLike(location) || "/";
}

export function sanitizeReturnTo(returnTo, fallback = "") {
  const decoded = safeDecode(returnTo).trim();
  if (!decoded) return fallback;
  if (!decoded.startsWith("/") || decoded.startsWith("//")) return fallback;
  if (/^[a-z][a-z0-9+.-]*:/i.test(decoded)) return fallback;
  if (decoded.includes("\\")) return fallback;

  try {
    const parsed = new URL(decoded, "https://lightly.local");
    if (parsed.origin !== "https://lightly.local") return fallback;
    if (isAuthRoute(parsed.pathname)) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function getReturnTo(location, fallback = "") {
  const params = new URLSearchParams(location?.search || "");
  const explicit = params.get("returnTo") || params.get("continueUrl") || params.get("redirect");
  const stateFrom = normalizeLocationLike(location?.state?.from || location?.state);
  return sanitizeReturnTo(explicit || stateFrom, fallback);
}

export function storeOrgLoginContext(locationOrPath, orgSlug = "") {
  if (typeof window === "undefined") return;
  const returnPath = sanitizeReturnTo(getCurrentPath(locationOrPath), "");
  if (!returnPath) return;

  const resolvedOrgSlug = orgSlug || inferOrgSlugFromPath(returnPath);
  if (resolvedOrgSlug) {
    setStorageSafe(window.sessionStorage, ORG_CONTEXT_SESSION_KEY, resolvedOrgSlug);
    setStorageSafe(window.localStorage, ORG_CONTEXT_LOCAL_KEY, resolvedOrgSlug);
  }
  setStorageSafe(window.sessionStorage, RETURN_PATH_SESSION_KEY, returnPath);
  setStorageSafe(window.localStorage, RETURN_PATH_LOCAL_KEY, returnPath);
  if (isStandaloneContext()) {
    setStorageSafe(window.sessionStorage, PWA_STANDALONE_SESSION_KEY, "true");
    setStorageSafe(window.localStorage, PWA_STANDALONE_LOCAL_KEY, "true");
  }
  storePostAuthReturnTo(returnPath);
}

export function getStoredOrgLoginContext({ consume = false } = {}) {
  if (typeof window === "undefined") return { orgSlug: "", returnPath: "" };
  const returnPath = sanitizeReturnTo(
    getStorageSafe(window.sessionStorage, RETURN_PATH_SESSION_KEY) ||
      getStorageSafe(window.localStorage, RETURN_PATH_LOCAL_KEY),
    ""
  );
  const orgSlug =
    getStorageSafe(window.sessionStorage, ORG_CONTEXT_SESSION_KEY) ||
    getStorageSafe(window.localStorage, ORG_CONTEXT_LOCAL_KEY) ||
    inferOrgSlugFromPath(returnPath);
  const pwaStandalone =
    getStorageSafe(window.sessionStorage, PWA_STANDALONE_SESSION_KEY) === "true" ||
    getStorageSafe(window.localStorage, PWA_STANDALONE_LOCAL_KEY) === "true";

  if (consume) {
    try {
      window.sessionStorage.removeItem(RETURN_PATH_SESSION_KEY);
      window.sessionStorage.removeItem(ORG_CONTEXT_SESSION_KEY);
      window.sessionStorage.removeItem(PWA_STANDALONE_SESSION_KEY);
    } catch {
      /* keep local fallback */
    }
  }

  return { orgSlug, returnPath, pwaStandalone };
}

export function buildLoginUrl(locationOrPath, options = {}) {
  const returnTo = sanitizeReturnTo(getCurrentPath(locationOrPath), "");
  storeOrgLoginContext(returnTo || locationOrPath, options.orgSlug || "");
  const params = new URLSearchParams();
  if (returnTo) params.set('returnTo', returnTo);
  if (options.orgSlug) params.set('org', options.orgSlug);
  const search = params.toString();
  return `/login${search ? `?${search}` : ''}`;
}

export function buildRegisterUrl(locationOrPath, options = {}) {
  const returnTo = sanitizeReturnTo(getCurrentPath(locationOrPath), "");
  storeOrgLoginContext(returnTo || locationOrPath, options.orgSlug || "");
  const suffix = returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : "";
  return `/login?mode=register${suffix}${options.orgSlug ? `&org=${encodeURIComponent(options.orgSlug)}` : ''}`;
}

export function storePostAuthReturnTo(returnTo) {
  const safeReturnTo = sanitizeReturnTo(returnTo, "");
  if (!safeReturnTo) return;
  try {
    window.localStorage.setItem(POST_AUTH_RETURN_TO_KEY, safeReturnTo);
  } catch {
    /* ignore storage failures */
  }
}

export function consumePostAuthReturnTo() {
  try {
    const stored = window.localStorage.getItem(POST_AUTH_RETURN_TO_KEY);
    window.localStorage.removeItem(POST_AUTH_RETURN_TO_KEY);
    return sanitizeReturnTo(stored, "");
  } catch {
    return "";
  }
}

export function resolvePostAuthRedirect(user, returnTo, membership, fallbackOrgId = "") {
  return sanitizeReturnTo(returnTo, "") || getPostLoginRoute(user, membership, fallbackOrgId);
}

export function getPostLoginRoute(user, membership, fallbackOrgId = "") {
  if (!user) return "/login";

  const orgId =
    membership?.orgSlug ||
    membership?.organizationSlug ||
    membership?.orgId ||
    membership?.organizationId ||
    fallbackOrgId ||
    "";

  if (!orgId) return "/platform/onboarding";

  const role = String(membership?.role || "").toLowerCase();
  if (["children_ministry_worker", "children_ministry_leader"].includes(role)) return `/platform/o/${orgId}/children-teachers`;
  if (role === "owner" || role === "admin" || membership?.permissions?.canViewDashboard) {
    return `/platform/o/${orgId}/dashboard`;
  }

  if (role === "member" || role === "custom") {
    return `/${orgId}/me`;
  }

  return `/${orgId}`;
}
